import {
  formatCurrencyFr, formatDaysFr, formatNumberFr, formatPercentageFr,
} from '@/lib/formatters';
import {
  formatKpiValue, performanceTier, TIER_BORDER_CLASS, TIER_COLOR_HEX,
} from '../utils';

const formatters = { formatCurrencyFr, formatDaysFr, formatNumberFr, formatPercentageFr };

/**
 * KPI card en grande forme — utilisée par les onglets familles du rapport.
 *
 * Structure (alignée sur le brief produit) :
 *   - Liseré gauche 2px, couleur selon percentile (vert ≥60 / brun ≥40 / bordeaux <40)
 *   - Top : label à gauche, "vs benchmark X" à droite
 *   - Milieu : valeur serif 22px + unité plus petite
 *   - Barre de progression 4px avec marker à 50 (médiane benchmark)
 *   - Footer : écart annualisé en € + percentile (placeholder pour la
 *     tendance M-1 quand l'historique sera enrichi en étape 7).
 */
export default function KpiBigCard({ kpi }) {
  if (!kpi) return null;
  const tier = performanceTier(kpi.percentile);
  const tierBorder = TIER_BORDER_CLASS[tier];
  const tierColor = TIER_COLOR_HEX[tier];

  const benchmarkFormatted = formatKpiValue({ ...kpi, value: kpi.cohort_median }, formatters);
  const valueFormatted = formatKpiValue(kpi, formatters);

  const percentileWidth = Math.max(0, Math.min(100, kpi.percentile || 0));
  const gapEuros = kpi.gap_euros;
  const hasGap = typeof gapEuros === 'number' && Math.abs(gapEuros) >= 1;

  return (
    <div
      className={`bg-cabinet-cream-dark border-y border-r border-cabinet-border border-l-2 ${tierBorder} p-5`}
      data-testid={`kpi-big-${kpi.kpi_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="text-[11px] text-cabinet-ink leading-tight font-medium pr-2">
          {kpi.label}
        </div>
        <div className="text-[9px] text-cabinet-ink-soft uppercase tracking-[0.18em] text-right shrink-0">
          vs benchmark
          <div className="font-medium text-cabinet-ink-secondary normal-case tracking-normal mt-0.5">
            {benchmarkFormatted}
          </div>
        </div>
      </div>

      <div className="flex items-baseline gap-2 mb-4">
        <span className="font-playfair text-[22px] text-cabinet-ink leading-none">
          {valueFormatted}
        </span>
      </div>

      <div className="relative h-1 bg-cabinet-paper border border-cabinet-border/60 mb-4 overflow-hidden">
        <div
          className="h-full transition-all duration-500"
          style={{ width: `${percentileWidth}%`, background: tierColor }}
        />
        <div
          className="absolute top-0 bottom-0 w-px bg-cabinet-ink-soft/50"
          style={{ left: '50%' }}
          aria-hidden
        />
      </div>

      <div className="flex items-center justify-between text-[9px] uppercase tracking-[0.18em] text-cabinet-ink-soft">
        <span>
          {kpi.percentile != null
            ? <>Percentile <span className="text-cabinet-ink-secondary">{Math.round(kpi.percentile)}</span></>
            : 'Cohorte indisponible'}
        </span>
        {hasGap && (
          <span style={{ color: gapEuros >= 0 ? '#4A6B3A' : '#8B0028' }}>
            {gapEuros >= 0 ? '+' : '−'}{formatCurrencyFr(Math.abs(gapEuros))} / an
          </span>
        )}
      </div>
    </div>
  );
}
